/**
 * SEO and JSON-LD for the public contact page (`/contact`).
 * Matches place detail SEO: `seo()`, `robotsMetaForEnvironment`, absolute URLs, and JSON-LD array.
 */

import { env } from '@web/src/lib/env.client'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { absoluteUrlForPath } from '@web/src/lib/utilsClient'

const CONTACT_PAGE_DESCRIPTION =
  'Contact Spicy Friends for billing and account help. Email support@spicyfriends.com. Copyright notices: dmca@spicyfriends.com. Legal and privacy: legal@spicyfriends.com. Safety: safety@spicyfriends.com.'

function buildContactBreadcrumbJsonLd(pageUrl: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrlForPath('/') },
      { '@type': 'ListItem', position: 2, name: 'Contact', item: pageUrl },
    ],
  }
}

function buildContactPageJsonLd(pageUrl: string, description: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact',
    description,
    url: pageUrl,
    image: absoluteUrlForPath('/logo-og-image.webp'),
    mainEntity: {
      '@type': 'Organization',
      name: 'Spicy Friends',
      url: absoluteUrlForPath('/'),
      email: 'support@spicyfriends.com',
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'support@spicyfriends.com',
        },
        {
          '@type': 'ContactPoint',
          contactType: 'legal',
          email: 'legal@spicyfriends.com',
        },
        {
          '@type': 'ContactPoint',
          contactType: 'copyright',
          email: 'dmca@spicyfriends.com',
        },
        {
          '@type': 'ContactPoint',
          contactType: 'safety',
          email: 'safety@spicyfriends.com',
        },
      ],
      address: {
        '@type': 'PostalAddress',
        streetAddress: '7242 Wellington Rd 124',
        addressLocality: 'Guelph',
        addressRegion: 'Ontario',
        postalCode: 'N1H 6J3',
        addressCountry: 'CA',
      },
    },
  }
}

/** Loader fields reserved for future `/contact` head data (e.g. CMS copy). */
export type ContactPageSeoLoaderInput = Record<string, never>

export function contactPageHeadFromLoaderData(_loaderData?: ContactPageSeoLoaderInput | null | undefined) {
  const pageUrl = absoluteUrlForPath('/contact')
  const description = CONTACT_PAGE_DESCRIPTION
  const contactPage = buildContactPageJsonLd(pageUrl, description)
  const breadcrumbList = buildContactBreadcrumbJsonLd(pageUrl)

  return seo({
    title: `Contact & support | ${env.VITE_APP_NAME}`,
    description,
    url: pageUrl,
    image: '/logo-og-image.webp',
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd: [contactPage, breadcrumbList],
  })
}
